import React, { useState } from 'react';
import { Pagination } from '../common/Pagination';
import { SortableHeader } from '../common/SortableHeader';
import { ListEmpty, ListErrorCard, ListLoadingCard } from '../common/ListStateCard';
import { useI18n } from '../../contexts/I18nContext';
import { typesService, HardwareType, HardwareTypeCreate } from '../../services/typesService';
import { HardwareTypeDetailModal } from './HardwareTypeDetailModal';
import { AddHardwareTypeModal } from './AddHardwareTypeModal';
import { useHardwareTypesList, HardwareSortField } from './hardwareTypesList/useHardwareTypesList';

interface HardwareTypesTableProps {
  canEdit?: boolean;
  refreshTrigger?: number;
  onTypeSelect?: (hardwareType: HardwareType) => void;
}

/**
 * The hardware types, one row per product: code, name, its model
 * references, how many devices run it. The data lives in
 * `useHardwareTypesList`; a row opens the type's dialog.
 */
export const HardwareTypesTable: React.FC<HardwareTypesTableProps> = ({ canEdit = false, refreshTrigger, onTypeSelect }) => {
  const { formatDate, formatNumber, t } = useI18n();
  const list = useHardwareTypesList(refreshTrigger);
  const [selectedType, setSelectedType] = useState<HardwareType | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);

  const openType = (type: HardwareType) => {
    setSelectedType(type);
    onTypeSelect?.(type);
  };

  const sortHeader = (field: HardwareSortField, label: string, numeric = false) => (
    <SortableHeader field={field} label={label} activeField={list.sort.field} direction={list.sort.direction} onSort={list.sortBy} numeric={numeric} />
  );

  if (list.loading) return <ListLoadingCard message={t('deviceTypes.loadingHardwareTypes')} />;
  if (list.error) return <ListErrorCard title={t('deviceTypes.errorLoadingHardware')} message={list.error} onRetry={list.reload} />;

  const { facts } = list;

  return (
    <div className="tpl-container hardware-types-container">
      <div className={`tpl-card${list.refetching ? ' is-refetching' : ''}`}>
        <div className="tpl-toolbar">
          <input
            type="search"
            data-testid="field-hardware-types-search"
            placeholder={t('deviceTypes.searchHardwareTypes')}
            value={list.searchTerm}
            onChange={(e) => list.setSearchTerm(e.target.value)}
            className="tpl-search"
            aria-label={t('deviceTypes.searchHardwareTypes')}
          />
          {list.canSeeInactive && (
            <label className="tpl-check" title={t('common.showInactiveHint')}>
              <input type="checkbox" checked={list.showInactive} onChange={(e) => list.setShowInactive(e.target.checked)} />
              {t('common.showInactive')}
            </label>
          )}
          <div className="tpl-facts" data-testid="hardware-types-facts">
            <span><strong>{formatNumber(facts.total)}</strong> {t('deviceTypes.facts.types')}</span>
            <span className="is-ok"><strong>{formatNumber(facts.active)}</strong> {t('deviceTypes.facts.active')}</span>
            {facts.inactive > 0 && <span className="is-warning"><strong>{formatNumber(facts.inactive)}</strong> {t('deviceTypes.facts.inactive')}</span>}
            <span><strong>{formatNumber(facts.modelRefs)}</strong> {t('deviceTypes.facts.modelRefs')}</span>
          </div>
          {canEdit && (
            <button type="button" className="dt-add" data-testid="btn-add-hardware-type" onClick={() => setShowAddModal(true)}>
              <span aria-hidden="true">+</span> {t('deviceTypes.addHardwareType')}
            </button>
          )}
        </div>

        <div className="tpl-table-wrap">
          <table className="tpl-table" data-testid="hardware-types-table">
            <thead>
              <tr>
                {sortHeader('name', t('deviceTypes.columns.type'))}
                {sortHeader('code', t('deviceTypes.columns.code'))}
                <th>{t('deviceTypes.columns.modelRefs')}</th>
                {sortHeader('device_count', t('deviceTypes.columns.devices'), true)}
                {list.showInactive && sortHeader('is_active', t('deviceTypes.columns.status'))}
                {sortHeader('updated_at', t('deviceTypes.columns.updated'))}
                <th className="tpl-col-open"><span className="sr-only">{t('deviceTypes.open')}</span></th>
              </tr>
            </thead>
            <tbody>
              {list.rows.map((type) => {
                const refs = list.modelRefs[type.id] || [];
                return (
                  <tr key={type.id} className={`tpl-row${type.is_active ? '' : ' is-off'}`} data-testid={`row-hardware-type-${type.code}`} onClick={() => openType(type)}>
                    <td className="tpl-cell-name">
                      <div className="tpl-name-line"><strong className="tpl-name">{type.name}</strong></div>
                      {type.description && <span className="tpl-sub">{type.description}</span>}
                    </td>
                    <td><span className="type-badge tpl-type">{type.code}</span></td>
                    <td className="dt-refs-cell">
                      {refs.length === 0 ? (
                        <span className="tpl-muted">–</span>
                      ) : (
                        <div className="dt-refs">
                          {refs.slice(0, 3).map((ref) => <span key={ref.id} className="dt-ref-chip" title={ref.name || ref.code}>{ref.code}</span>)}
                          {refs.length > 3 && <span className="dt-ref-more">+{refs.length - 3}</span>}
                        </div>
                      )}
                    </td>
                    <td className="numeric tpl-cell-count">
                      <span className="tpl-count">{formatNumber(type.device_count || 0)}</span>
                    </td>
                    {list.showInactive && (
                      <td>
                        <span className={`status-badge ${type.is_active ? 'active' : 'inactive'}`}>
                          {type.is_active ? t('common.active') : t('common.inactive')}
                        </span>
                      </td>
                    )}
                    <td className="tpl-muted">{formatDate(type.updated_at)}</td>
                    <td className="tpl-col-open"><span className="tpl-chevron" aria-hidden="true">›</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {list.rows.length === 0 && (
            <ListEmpty title={t('deviceTypes.noHardwareTypesFound')} hint={list.searchTerm ? t('deviceTypes.emptyHint') : undefined} />
          )}
        </div>

        <Pagination
          page={list.currentPage}
          totalPages={list.serverTotalPages}
          totalItems={list.serverTotalItems}
          pageSize={list.itemsPerPage}
          itemLabel="hardware types"
          onPageChange={list.setCurrentPage}
          onPageSizeChange={(size) => { list.setItemsPerPage(size); list.setCurrentPage(1); }}
        />
      </div>

      <AddHardwareTypeModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onCreated={list.reload}
        onCreate={(data: HardwareTypeCreate) => typesService.createHardwareType(data).then(() => undefined)}
      />

      <HardwareTypeDetailModal
        hardwareType={selectedType}
        modelRefs={selectedType ? (list.modelRefs[selectedType.id] || []) : []}
        isOpen={selectedType !== null}
        canEdit={canEdit}
        onClose={() => setSelectedType(null)}
        onTypeUpdated={() => { setSelectedType(null); list.reload(); }}
      />
    </div>
  );
};
